/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import type { Route } from './+types/_app.page.$slug';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { Region } from '@/components/region';
import {
    HEADER_UNDERLAY_NEGATIVE_MARGIN_CLASS,
    pageHasContentPageHeaderUnderlay,
} from '@/lib/page-designer/content-page-header-underlay';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
export { shouldRevalidate } from '@/lib/revalidation/routes/content-page';

@PageType({
    name: 'Content Page',
    description: 'Standalone content page where the URL slug is the Page Designer page ID.',
    supportedAspectTypes: [],
})
@RegionDefinition([
    { id: 'header', name: 'Header Region', maxComponents: 5 },
    { id: 'main', name: 'Main Region', maxComponents: 10 },
])
export class ContentPageMetadata {}

export type ContentPageData = {
    page: ReturnType<typeof fetchPageWithComponentData>;
    /** True when Header Region contains hero / carousel / image / video / YouTube — enables home-style header underlay. */
    headerUnderlay: boolean;
    wishlistInitialState: Promise<WishlistInitialState>;
};

/**
 * Server-side loader that resolves the Page Designer page whose ID matches the
 * `:slug` URL param. No aspect attributes are passed — this is a standalone
 * page, the same category as `homepage`/`aboutus`, just with a dynamic pageId.
 */
export async function loader(args: Route.LoaderArgs): Promise<ContentPageData> {
    const { slug = '' } = args.params;
    const pagePromise = fetchPageWithComponentData(args, { pageId: slug });
    const [page, wishlistInitialState] = await Promise.all([
        pagePromise,
        fetchWishlistInitialState(args.context),
    ]);

    return {
        page,
        headerUnderlay: pageHasContentPageHeaderUnderlay(page),
        wishlistInitialState,
    };
}

/**
 * Content Page - a generic, merchant-managed Page Designer page reachable at
 * `/page/:slug`. Product tiles inside PD components require WishlistProvider
 * (same as homepage, PLP, and search).
 */
export default function ContentPage({ loaderData }: { loaderData: ContentPageData }): ReactElement {
    const { page, headerUnderlay, wishlistInitialState } = loaderData;

    const regions = (
        <>
            <Region page={page} regionId="header" />
            <Region page={page} regionId="main" />
        </>
    );

    return (
        <WishlistProvider initialState={wishlistInitialState}>
            {headerUnderlay ? (
                <div className={HEADER_UNDERLAY_NEGATIVE_MARGIN_CLASS} data-testid="content-page-header-underlay">
                    {regions}
                </div>
            ) : (
                regions
            )}
        </WishlistProvider>
    );
}
